import {
  type CollectionFileSummary,
  fetchDataSourceFile,
  fetchDataSourceFilePreview,
  fetchFileMarkdown,
  fetchSpaceFile,
  fetchSpaceFileMarkdown,
  fetchSpaceFilePreview,
} from "@/api";
import { extensionOf } from "@/features/chat/documents/documentInfo";
import type { PreviewFile } from "@/features/chat/documents/previewFile";
import { type TextKind, textKindOf } from "@/features/chat/documents/textDocuments";

/** A knowledge-base file: its original, its PDF rendering, and the text extracted from it for search. */
export interface KnowledgeFile extends PreviewFile {
  markdown: () => Promise<string>;
}

/** How a file is shown: pages from the server's PDF, rendered text, the image itself, or the extracted text. */
export type FileView = { kind: "pages" } | { kind: "text"; text: TextKind } | { kind: "image" } | { kind: "extracted" };

const PAGE_EXTENSIONS = new Set(["pdf", "docx", "doc", "xlsx", "xls", "pptx", "ppt"]);
const IMAGE_EXTENSIONS = new Set(["png", "jpg", "jpeg", "gif", "webp"]);

const EXTENSION_BY_MIME: Record<string, string> = {
  "application/pdf": "pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  "application/msword": "doc",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.ms-excel": "xls",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "pptx",
  "application/vnd.ms-powerpoint": "ppt",
  "text/markdown": "md",
  "text/csv": "csv",
  "text/html": "html",
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/gif": "gif",
  "image/webp": "webp",
};

/** Website pages and notes are named by title or URL, so the MIME type wins over the name. */
function fileExtension(file: CollectionFileSummary): string {
  const mime = file.mimeType?.split(";")[0].trim().toLowerCase();
  return (mime && EXTENSION_BY_MIME[mime]) || extensionOf(file.filename);
}

export function fileViewOf(file: CollectionFileSummary): FileView {
  const ext = fileExtension(file);
  if (PAGE_EXTENSIONS.has(ext)) return { kind: "pages" };
  if (IMAGE_EXTENSIONS.has(ext)) return { kind: "image" };
  const text = textKindOf(ext);
  return text ? { kind: "text", text } : { kind: "extracted" };
}

export function dataSourceFile(collectionUuid: string, file: CollectionFileSummary): KnowledgeFile {
  return {
    key: ["data-sources", collectionUuid, "files", file.uuid, file.fileSize ?? 0],
    original: () => fetchDataSourceFile(collectionUuid, file.uuid),
    pdf: () => fetchDataSourceFilePreview(collectionUuid, file.uuid),
    markdown: () => fetchFileMarkdown(collectionUuid, file.uuid),
  };
}

export function spaceFile(spaceUuid: string, file: CollectionFileSummary): KnowledgeFile {
  return {
    key: ["spaces", spaceUuid, "files", file.uuid, file.fileSize ?? 0],
    original: () => fetchSpaceFile(spaceUuid, file.uuid),
    pdf: () => fetchSpaceFilePreview(spaceUuid, file.uuid),
    markdown: () => fetchSpaceFileMarkdown(spaceUuid, file.uuid),
  };
}
